// src/App.js
import React, { useState, useEffect, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useLocation } from 'react-router-dom';
import './App.css';
import SplitScroll from './SplitScroll';
import Tryouts from './Tryouts';
import logo from './assets/images/gladiators-hero.png';

// Firebase
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { auth } from './firebase';

// Pages
import About from './About';
import Roster from './Roster';
import JoinUs from './JoinUs';
import GladiatorGames from './GladiatorGames';
import ParentsOnly from './ParentsOnly';
import ParentChatPage from './ParentChatPage';
import Login from './Login';
import SignUp from './SignUp';
import ResetPassword from './ResetPassword';
import ScheduleGame from './ScheduleGame';
import AdminPortal from './AdminPortal';
import ParentUpload from './ParentUpload';
import Gallery from './Gallery';
import ProtectedRoute from './ProtectedRoute';
import TeamStore from './pages/TeamStore';
import Cart from './pages/Cart';
import Checkout from './pages/Checkout';
import HockeyStatTracker from './pages/HockeyStatTracker';
import LiveScoreboard from './pages/LiveScoreboard';
import GladiatorsTV from './pages/GladiatorsTV';
import GladiatorGazette from './pages/GladiatorGazette';
import GladiatorMemoryGame from './pages/GladiatorMemoryGame';
import PlayerCardGenerator from './components/playerCard/PlayerCardGenerator';
import PwaUpdateBanner from './components/PwaUpdateBanner';
import StatsSandboxBanner from './components/StatsSandboxBanner';

import { CartProvider, useCart } from './context/CartContext';

const GLADIATOR_BANNER_END = new Date('2026-03-23'); // After 3/22/26 (midnight 3/23) hide banner

const NavCloser = ({ onClose, children }) => {
  const location = useLocation();
  useEffect(() => {
    onClose();
  }, [location.pathname, onClose]);
  return children;
};

const App = () => {
  const [currentUser, setCurrentUser] = useState(null);
  const [navbarExpanded, setNavbarExpanded] = useState(false);
  const [bannerDismissed, setBannerDismissed] = useState(() => {
    if (new Date() >= GLADIATOR_BANNER_END) return true;
    return typeof window !== 'undefined' && localStorage.getItem('gladiatorBannerDismissed') === 'true';
  });

  const showGladiatorBanner = !bannerDismissed && new Date() < GLADIATOR_BANNER_END;

  const dismissGladiatorBanner = () => {
    if (typeof window !== 'undefined') localStorage.setItem('gladiatorBannerDismissed', 'true');
    setBannerDismissed(true);
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, user => setCurrentUser(user));
    return unsubscribe;
  }, []);

  const handleLogout = async () => {
    try {
      await signOut(auth);
    } catch (err) {
      console.error('Logout failed', err);
    }
    setNavbarExpanded(false);
  };

  const handleNavLinkClick = useCallback(() => setNavbarExpanded(false), []);

  const handleNavClose = useCallback(() => setNavbarExpanded(false), []);

  const CartNavLink = () => {
    const { cartItems } = useCart();
    if (cartItems.length === 0) return null;
    return (
      <li className="nav-item">
        <Link to="/cart" className="nav-link text-white" onClick={handleNavLinkClick}>
          Cart
          <span className="badge bg-warning ms-1">
            {cartItems.reduce((sum, item) => sum + item.quantity, 0)}
          </span>
        </Link>
      </li>
    );
  };

  return (
    <CartProvider>
      <Router>
        <NavCloser onClose={handleNavClose}>
        <PwaUpdateBanner />
        <StatsSandboxBanner />
        <div className={`App${showGladiatorBanner ? ' has-gladiator-banner' : ''}`}>
          {showGladiatorBanner && (
            <div className="gladiator-banner">
              <div className="gladiator-banner-inner">
                <span className="gladiator-banner-text">
                  Gladiator Games – March 21, 2026 at Huntington Center. 12U & 14U – skate where the pros play.
                </span>
                <Link to="/gladiator-games" className="gladiator-banner-cta" onClick={handleNavLinkClick}>
                  I'm interested
                </Link>
                <button
                  type="button"
                  className="gladiator-banner-dismiss"
                  onClick={dismissGladiatorBanner}
                  aria-label="Dismiss banner"
                >
                  ×
                </button>
              </div>
            </div>
          )}
          <nav className="navbar navbar-expand-lg navbar-dark bg-teal fixed-top">
            <div className="container-fluid">
              <Link to="/" className="navbar-brand d-flex align-items-center" style={{ gap: 10 }}>
                <img src={logo} alt="Glass City Gladiators" style={{ height: 48 }} />
                <span
                  style={{
                    color: '#FFD700',
                    fontSize: '1.2rem',
                    fontWeight: 'bold',
                    letterSpacing: '1px',
                    textShadow: '0 0 6px rgba(255,215,0,0.4)',
                  }}
                >
                  Glass City Gladiators
                </span>
              </Link>

              <button
                className="navbar-toggler"
                type="button"
                onClick={() => setNavbarExpanded(prev => !prev)}
                aria-controls="navbarNav"
                aria-expanded={navbarExpanded}
                aria-label="Toggle navigation"
              >
                <span className="navbar-toggler-icon"></span>
              </button>

              <div className={`collapse navbar-collapse ${navbarExpanded ? 'show' : ''}`} id="navbarNav">
                <ul className="navbar-nav ms-auto">
                  <li className="nav-item dropdown">
                    <span className="nav-link dropdown-toggle text-white" role="button" data-bs-toggle="dropdown">
                      Team Info
                    </span>
                    <ul className="dropdown-menu dropdown-menu-end">
                      <li>
                        <Link className="dropdown-item" to="/roster" onClick={handleNavLinkClick}>
                          Roster
                        </Link>
                      </li>
                    </ul>
                  </li>

                  <li className="nav-item dropdown">
                    <span className="nav-link dropdown-toggle text-white" role="button" data-bs-toggle="dropdown">
                      Get Involved
                    </span>
                    <ul className="dropdown-menu dropdown-menu-end">
                      <li>
                        <Link className="dropdown-item" to="/schedule-game" onClick={handleNavLinkClick}>
                          Schedule A Game
                        </Link>
                      </li>
                      <li>
                        <Link className="dropdown-item" to="/build-your-gladiator" onClick={handleNavLinkClick}>
                          Build Your Gladiator
                        </Link>
                      </li>
                    </ul>
                  </li>

                  <li className="nav-item">
                    <Link to="/admin" className="nav-link text-white" onClick={handleNavLinkClick}>
                      Admin
                    </Link>
                  </li>

                  <li className="nav-item">
                    <Link to="/store" className="nav-link text-white" onClick={handleNavLinkClick}>
                      Team Store
                    </Link>
                  </li>

                  <li className="nav-item">
                    <Link to="/memory-game" className="nav-link text-white" onClick={handleNavLinkClick}>
                      Memory Game
                    </Link>
                  </li>

                  <li className="nav-item">
                    <Link to="/stats" className="nav-link text-white" onClick={handleNavLinkClick}>
                      Stats
                    </Link>
                  </li>

                  <li className="nav-item">
                    <Link to="/gladiators-tv" className="nav-link gladiators-tv-nav" onClick={handleNavLinkClick}>
                      Gladiators TV <span className="nav-new-badge">New</span>
                    </Link>
                  </li>

                  <li className="nav-item">
                    <Link to="/gladiator-gazette" className="nav-link text-white" onClick={handleNavLinkClick}>
                      Gazette
                    </Link>
                  </li>

                  <CartNavLink />

                  {currentUser ? (
                    <>
                      <li className="nav-item dropdown">
                        <span className="nav-link dropdown-toggle text-white" role="button" data-bs-toggle="dropdown">
                          Parents
                        </span>
                        <ul className="dropdown-menu dropdown-menu-end">
                          <li>
                            <Link className="dropdown-item" to="/parents-only" onClick={handleNavLinkClick}>
                              Parents Only
                            </Link>
                          </li>
                          <li>
                            <Link className="dropdown-item" to="/upload" onClick={handleNavLinkClick}>
                              Upload Photo
                            </Link>
                          </li>
                          <li>
                            <Link className="dropdown-item" to="/gallery" onClick={handleNavLinkClick}>
                              Gallery
                            </Link>
                          </li>
                          <li>
                            <Link className="dropdown-item" to="/parents-only/chat" onClick={handleNavLinkClick}>
                              Team Discussions
                            </Link>
                          </li>

                        </ul>
                      </li>

                      <li className="nav-item">
                        <button onClick={handleLogout} className="nav-link text-white btn btn-teal">
                          Log Out
                        </button>
                      </li>
                    </>
                  ) : (
                    <>
                      <li className="nav-item">
                        <Link to="/login" className="nav-link text-white btn btn-teal" onClick={handleNavLinkClick}>
                          Log In
                        </Link>
                      </li>
                      <li className="nav-item">
                        <Link to="/signup" className="nav-link text-white btn btn-teal" onClick={handleNavLinkClick}>
                          Sign Up
                        </Link>
                      </li>
                    </>
                  )}
                </ul>
              </div>
            </div>
          </nav>

          <div className="container mt-5 pt-5">
            <Routes>
              <Route path="/" element={<SplitScroll />} />
              <Route path="/about" element={<About />} />
              <Route path="/roster" element={<Roster />} />
              <Route path="/tryouts" element={<Tryouts />} />
              <Route path="/join-us" element={<JoinUs />} />
              <Route path="/schedule-game" element={<ScheduleGame />} />
              <Route path="/gladiator-games" element={<GladiatorGames />} />
              <Route path="/login" element={<Login />} />
              <Route path="/signup" element={<SignUp />} />
              <Route path="/reset-password" element={<ResetPassword />} />

              <Route
                path="/parents-only"
                element={
                  <ProtectedRoute>
                    <ParentsOnly />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/upload"
                element={
                  <ProtectedRoute>
                    <ParentUpload />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/gallery"
                element={
                  <ProtectedRoute>
                    <Gallery />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/parents-only/chat"
                element={
                  <ProtectedRoute>
                    <ParentChatPage />
                  </ProtectedRoute>
                }
              />

<Route path="/admin" element={<AdminPortal />} />
<Route path="/store" element={<TeamStore />} />
<Route path="/cart" element={<Cart />} />
<Route path="/checkout" element={<Checkout />} />


              <Route path="/stats" element={<HockeyStatTracker teamId="glass-city-gladiators-12u" />} />
              <Route path="/live" element={<LiveScoreboard teamId="glass-city-gladiators-12u" />} />
              <Route path="/gladiators-tv" element={<GladiatorsTV />} />
              <Route path="/gladiator-gazette" element={<GladiatorGazette />} />
              <Route path="/memory-game" element={<GladiatorMemoryGame teamId="glass-city-gladiators-12u" />} />
              <Route path="/build-your-gladiator" element={<PlayerCardGenerator />} />
            </Routes>
          </div>
        </div>
        </NavCloser>
      </Router>
    </CartProvider>
  );
};

export default App;
